import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { FiArrowLeft, FiImage } from 'react-icons/fi';
import Layout from '../Layout/Layout';
import myAxios from '../AxiosUrl';
import { uploadCallback, ImageUpload } from '../AddProduct/HelperFunction';
import styles from './Blog.module.css';

function EditBlog() {
    const navigate = useNavigate();
    const { id } = useParams();
    const uid = localStorage.getItem('id');

    const [form, setForm] = useState(null);
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [coverImage, setCoverImage] = useState('');
    const [coverUploading, setCoverUploading] = useState(false);
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [keywords, setKeywords] = useState([]);
    const [keywordInput, setKeywordInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState(null);
    const coverRef = useRef();

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    useEffect(() => {
        myAxios.get(`/blog-single/${uid}/${id}`)
            .then((res) => {
                if (res.data.success) {
                    const b = res.data.blog;
                    setForm({
                        title: b.title || '',
                        excerpt: b.excerpt || '',
                        category: b.category || '',
                        author: b.author || '',
                        status: b.status || 'draft',
                        metaTitle: b.metaTitle || '',
                        metaDescription: b.metaDescription || '',
                    });
                    setCoverImage(b.coverImage || '');
                    setTags(b.tags || []);
                    setKeywords(b.focusKeywords || []);

                    // Load HTML content into editor
                    if (b.content) {
                        const blocksFromHtml = htmlToDraft(b.content);
                        if (blocksFromHtml) {
                            const { contentBlocks, entityMap } = blocksFromHtml;
                            const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
                            setEditorState(EditorState.createWithContent(contentState));
                        }
                    }
                } else {
                    showToast('Blog post not found', 'error');
                    navigate('/blog-list');
                }
            })
            .catch(() => {
                showToast('Failed to load post', 'error');
                navigate('/blog-list');
            })
            .finally(() => setLoading(false));
    }, [id, uid, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleCoverUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setCoverUploading(true);
        try {
            const url = await ImageUpload(file);
            setCoverImage(url);
        } catch {
            showToast('Cover image upload failed', 'error');
        } finally {
            setCoverUploading(false);
        }
    };

    const addTag = () => {
        const t = tagInput.trim();
        if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
        setTagInput('');
    };
    const removeTag = (t) => setTags((prev) => prev.filter((x) => x !== t));
    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
    };

    const addKeyword = () => {
        const k = keywordInput.trim();
        if (k && !keywords.includes(k)) setKeywords((prev) => [...prev, k]);
        setKeywordInput('');
    };
    const removeKeyword = (k) => setKeywords((prev) => prev.filter((x) => x !== k));
    const handleKeywordKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addKeyword(); }
    };

    const validate = () => {
        const errs = {};
        if (!form.title.trim()) errs.title = 'Title is required';
        return errs;
    };

    const buildPayload = (statusOverride) => ({
        ...form,
        content: draftToHtml(convertToRaw(editorState.getCurrentContent())),
        coverImage,
        tags,
        focusKeywords: keywords,
        status: statusOverride || form.status,
    });

    const handleSubmit = async (e, statusOverride) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setSaving(statusOverride || form.status);
        try {
            const res = await myAxios.put(`/blog/${id}`, buildPayload(statusOverride));
            if (res.data.success) {
                showToast('Blog post updated!');
            } else {
                showToast(res.data.message || 'Update failed', 'error');
            }
        } catch (err) {
            showToast(err?.response?.data?.message || 'Update failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className={styles.loadingWrap}><div className={styles.spinner}></div></div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={styles.container}>
                {toast && (
                    <div className={`${styles.toast} ${styles['toast_' + toast.type]}`}>
                        {toast.msg}
                    </div>
                )}

                <div className={styles.formHeader}>
                    <button className={styles.backBtn} onClick={() => navigate('/blog-list')}><FiArrowLeft style={{verticalAlign:'middle'}} /> Back</button>
                    <h1 className={styles.pageTitle}>Edit Blog Post</h1>
                </div>

                <form onSubmit={(e) => handleSubmit(e, null)} className={styles.blogForm}>
                    <div className={styles.formLayout}>

                        {/* ── Main Column ── */}
                        <div className={styles.mainCol}>

                            {/* Content Card */}
                            <div className={styles.card}>
                                <h3 className={styles.cardTitle}>Content</h3>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Title *</label>
                                    <input
                                        name="title"
                                        className={`${styles.input} ${errors.title ? styles.inputError : ''}`}
                                        value={form.title}
                                        onChange={handleChange}
                                        placeholder="Enter blog post title"
                                    />
                                    {errors.title && <span className={styles.error}>{errors.title}</span>}
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Excerpt <span className={styles.hint}>(short summary shown in listing)</span></label>
                                    <textarea
                                        name="excerpt"
                                        className={styles.textarea}
                                        rows={3}
                                        value={form.excerpt}
                                        onChange={handleChange}
                                        placeholder="Brief summary..."
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Content</label>
                                    <div className={styles.editorWrapper}>
                                        <Editor
                                            editorState={editorState}
                                            onEditorStateChange={setEditorState}
                                            toolbar={{
                                                options: ['inline','blockType','list','textAlign','link','image','history'],
                                                inline: { options: ['bold','italic','underline','strikethrough'] },
                                                blockType: { options: ['Normal','H2','H3','H4','Blockquote','Code'] },
                                                image: {
                                                    uploadCallback,
                                                    previewImage: true,
                                                    alt: { present: true, mandatory: false },
                                                    inputAccept: 'image/*',
                                                },
                                            }}
                                            toolbarClassName={styles.editorToolbar}
                                            editorClassName={styles.editorContent}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* SEO Card */}
                            <div className={styles.card}>
                                <h3 className={styles.cardTitle}>SEO Settings</h3>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Meta Title <span className={styles.hint}>(defaults to post title)</span></label>
                                    <input
                                        name="metaTitle"
                                        className={styles.input}
                                        value={form.metaTitle}
                                        onChange={handleChange}
                                        placeholder="SEO title..."
                                        maxLength={60}
                                    />
                                    <div className={styles.charCount}>{form.metaTitle.length}/60</div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Meta Description</label>
                                    <textarea
                                        name="metaDescription"
                                        className={styles.textarea}
                                        rows={3}
                                        value={form.metaDescription}
                                        onChange={handleChange}
                                        placeholder="SEO description (160 characters)..."
                                        maxLength={160}
                                    />
                                    <div className={styles.charCount}>{form.metaDescription.length}/160</div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Focus Keywords</label>
                                    <div className={styles.keywordsWrap} onClick={() => document.getElementById('kwInputEdit').focus()}>
                                        {keywords.map((k) => (
                                            <span key={k} className={styles.keywordTag}>
                                                {k}
                                                <button type="button" className={styles.keywordTagRemove} onClick={() => removeKeyword(k)}>×</button>
                                            </span>
                                        ))}
                                        <input
                                            id="kwInputEdit"
                                            className={styles.keywordsInput}
                                            value={keywordInput}
                                            onChange={(e) => setKeywordInput(e.target.value)}
                                            onKeyDown={handleKeywordKeyDown}
                                            onBlur={addKeyword}
                                            placeholder={keywords.length === 0 ? 'Add keyword, press Enter' : ''}
                                        />
                                    </div>
                                    <span className={styles.hint}>Press Enter or comma to add</span>
                                </div>
                            </div>
                        </div>

                        {/* ── Side Column ── */}
                        <div className={styles.sideCol}>

                            {/* Publish Card */}
                            <div className={styles.card}>
                                <h3 className={styles.cardTitle}>Publish</h3>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Status</label>
                                    <select name="status" className={styles.select} value={form.status} onChange={handleChange}>
                                        <option value="draft">Draft</option>
                                        <option value="published">Published</option>
                                    </select>
                                </div>
                                <div className={styles.publishBtns}>
                                    <button type="submit" className={styles.btnSecondary} disabled={!!saving}>
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                    {form.status === 'draft' && (
                                        <button
                                            type="button"
                                            className={styles.btnPrimary}
                                            disabled={!!saving}
                                            onClick={(e) => handleSubmit(e, 'published')}
                                        >
                                            {saving === 'published' ? 'Publishing...' : 'Publish'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Cover Image Card */}
                            <div className={styles.card}>
                                <h3 className={styles.cardTitle}>Cover Image</h3>
                                <div className={styles.coverUploadArea} onClick={() => coverRef.current.click()}>
                                    {coverUploading && (
                                        <div className={styles.coverUploadingOverlay}>Uploading...</div>
                                    )}
                                    {coverImage ? (
                                        <>
                                            <img src={coverImage} alt="Cover" className={styles.coverPreview} />
                                            <button
                                                type="button"
                                                className={styles.removeCoverBtn}
                                                onClick={(e) => { e.stopPropagation(); setCoverImage(''); }}
                                            >
                                                Remove
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className={styles.coverUploadIcon}><FiImage /></div>
                                            <p className={styles.coverUploadText}><strong>Click to upload</strong> cover image</p>
                                            <p className={styles.coverUploadHint}>PNG, JPG, WEBP recommended</p>
                                        </>
                                    )}
                                    <input
                                        ref={coverRef}
                                        type="file"
                                        accept="image/*"
                                        className={styles.coverUploadInput}
                                        onChange={handleCoverUpload}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            </div>

                            {/* Details Card */}
                            <div className={styles.card}>
                                <h3 className={styles.cardTitle}>Details</h3>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Author</label>
                                    <input
                                        name="author"
                                        className={styles.input}
                                        value={form.author}
                                        onChange={handleChange}
                                        placeholder="Author name"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Category</label>
                                    <input
                                        name="category"
                                        className={styles.input}
                                        value={form.category}
                                        onChange={handleChange}
                                        placeholder="e.g. Tips, News, Tutorial"
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Tags <span className={styles.hint}>(press Enter to add)</span></label>
                                    <div className={styles.keywordsWrap} onClick={() => document.getElementById('tagInputEdit').focus()}>
                                        {tags.map((t) => (
                                            <span key={t} className={styles.keywordTag}>
                                                {t}
                                                <button type="button" className={styles.keywordTagRemove} onClick={() => removeTag(t)}>×</button>
                                            </span>
                                        ))}
                                        <input
                                            id="tagInputEdit"
                                            className={styles.keywordsInput}
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyDown={handleTagKeyDown}
                                            onBlur={addTag}
                                            placeholder={tags.length === 0 ? 'tag1, tag2...' : ''}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    );
}

export default EditBlog;
